/**
 * Template Workflow Panel Component
 * Main container for the template workflow UI with toggle visibility
 */

import { useEffect } from 'react';
import { TemplateWorkflowTimeline } from './TemplateWorkflowTimeline';
import { useTemplateWorkflowStore } from '@/stores/templateWorkflowStore';
import { useConversationStore } from '@/stores/conversationStore';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TemplateWorkflowPanelProps {
  conversationId: string;
  className?: string;
}

export function TemplateWorkflowPanel({ conversationId, className }: TemplateWorkflowPanelProps) {
  const {
    getTemplateWorkflowState,
    isTemplateWorkflowVisible,
    isTemplateWorkflowEnabled,
    setTemplateWorkflowVisibility,
    initTemplateWorkflow,
  } = useTemplateWorkflowStore();

  const { getConversationById } = useConversationStore();

  const workflowState = getTemplateWorkflowState(conversationId);
  const isVisible = isTemplateWorkflowVisible(conversationId);
  const isEnabled = isTemplateWorkflowEnabled(conversationId);

  // Check if conversation is template project
  const conversation = getConversationById(conversationId);
  const isStyleConversation = conversation?.collectionType === 'STYLE';
  const hasTemplateProject = conversation?.metadata?.hasStyleProject || isStyleConversation;

  // Initialize workflow if needed
  useEffect(() => {
    if (!workflowState && hasTemplateProject) {
      console.log('🔧 Initializing template workflow for conversation:', conversationId);
      initTemplateWorkflow(conversationId);
    }
  }, [conversationId, workflowState, hasTemplateProject, initTemplateWorkflow]);

  // Toggle visibility
  const handleToggleVisibility = () => {
    setTemplateWorkflowVisibility(conversationId, !isVisible);
  };

  // Don't render if workflow not enabled or doesn't exist
  if (!workflowState || !isEnabled || !hasTemplateProject) {
    return null;
  }

  // Get current step info
  const currentStepNumber = ['validation', 'layout_generation', 'design_document'].indexOf(workflowState.currentStep) + 1;
  const totalSteps = 3;

  return (
    <div className={cn('relative', className)}>
      {/* Sticky Header with Toggle */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-accent">
              <FileText className="w-4 h-4 text-primary dark:text-accent-foreground" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Template Workflow
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Step {currentStepNumber} of {totalSteps}
              </p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleVisibility}
            className="h-8 w-8 p-0"
          >
            {isVisible ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="mt-3 w-full bg-slate-200 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
          <div
            className="h-full transition-all duration-500 ease-out bg-primary"
            style={{
              width: `${(currentStepNumber / totalSteps) * 100}%`,
            }}
          />
        </div>
      </div>

      {/* Collapsible Content */}
      {isVisible && (
        <div className="p-4">
          <TemplateWorkflowTimeline
            workflowState={workflowState}
            conversationId={conversationId}
          />
        </div>
      )}
    </div>
  );
}
